/**
 * The Play Now leaderboard: points from finished games, today, this week or all time, for
 * everyone or just you and your friends. Signed-in players only; a guest's seat counts for nobody.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Leaderboard as Board, type LeaderboardPeriod } from '../lib/api';
import { AuthGate } from '../components/AuthGate';

export function Leaderboard() {
  return (
    <AuthGate title="Leaderboard">
      <LeaderboardInner />
    </AuthGate>
  );
}

const PERIODS: { id: LeaderboardPeriod; label: string }[] = [
  { id: 'day', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'lifetime', label: 'All time' },
];

function LeaderboardInner() {
  const [period, setPeriod] = useState<LeaderboardPeriod>('week');
  const [friends, setFriends] = useState(false);
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    api
      .getLeaderboard(period, friends)
      .then((b) => {
        if (live) setBoard(b);
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof Error ? err.message : 'Could not load the leaderboard.');
      });
    return () => {
      live = false;
    };
  }, [period, friends]);

  /** One way: they are not asked. The row turns into "Friend" on success. */
  async function add(accountId: string) {
    setError(null);
    try {
      await api.addFriendById(accountId);
      setBoard((b) => b && { ...b, rows: b.rows.map((r) => (r.accountId === accountId ? { ...r, friend: true } : r)) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that friend.');
    }
  }

  return (
    <div className="centered">
      <div className="col" style={{ width: 'min(34rem, 100%)', gap: '0.75rem' }}>
        <div className="card col">
          <h1>Leaderboard</h1>
          <div className="row" role="radiogroup" aria-label="Period" style={{ gap: '0.4rem' }}>
            {PERIODS.map((p) => (
              <label key={p.id} className="game-choice" style={{ flex: 1 }}>
                <input type="radio" name="period" checked={period === p.id} onChange={() => setPeriod(p.id)} />
                <strong>{p.label}</strong>
              </label>
            ))}
          </div>
          <label className="field-check">
            <input type="checkbox" checked={friends} onChange={(e) => setFriends(e.target.checked)} />
            <span>Only me and my friends</span>
          </label>
          {error ? <p className="error-text">{error}</p> : null}
          {board && board.rows.length === 0 ? (
            <p className="faint">No Play Now points yet{period === 'lifetime' ? '' : ' for this period'}.</p>
          ) : null}
          {board && board.rows.length > 0 ? (
            <ol className="show-standings">
              {board.rows.map((r) => (
                <li key={r.accountId} className={r.accountId === board.you ? 'is-you' : undefined}>
                  <span className="show-rank">{r.rank}</span>
                  <span className="show-name">
                    {r.displayName}
                    {r.accountId === board.you ? (
                      <span className="faint"> (you)</span>
                    ) : r.friend ? (
                      <span className="faint"> · friend</span>
                    ) : (
                      <button
                        className="btn btn-ghost btn-sm lb-add"
                        type="button"
                        aria-label={`Add ${r.displayName} as a friend`}
                        onClick={() => void add(r.accountId)}
                      >
                        + Add friend
                      </button>
                    )}
                  </span>
                  <span className="faint">
                    {r.games} game{r.games === 1 ? '' : 's'}
                  </span>
                  <span className="lb-score">{r.points}</span>
                </li>
              ))}
            </ol>
          ) : null}
          <p className="faint" style={{ margin: 0 }}>
            Points from finished Play Now games while signed in.
          </p>
        </div>
        <div className="row" style={{ gap: '0.4rem' }}>
          <Link className="btn btn-ghost" to="/play-now" style={{ flex: 1 }}>
            Play Now
          </Link>
          <Link className="btn btn-ghost" to="/friends" style={{ flex: 1 }}>
            Friends
          </Link>
        </div>
      </div>
    </div>
  );
}
